import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  ScanLine,
  History,
  FileText,
  Languages,
  Lightbulb,
  Layers,
  LayoutDashboard,
  LogOut,
  Send,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import "./shell.css";

// Карточки результата живут на своих адресах, но для навигации это
// «Распознавание».
const NAV = [
  { to: "/", label: "Рабочий стол", Icon: LayoutGrid, match: ["/"] },
  {
    to: "/recognition",
    label: "Распознавание",
    Icon: ScanLine,
    match: ["/recognition", "/passport/", "/dl/", "/doc/", "/contract/"],
  },
  { to: "/history", label: "История", Icon: History, match: ["/history"] },
  { to: "/templates", label: "Шаблоны", Icon: FileText, match: ["/templates"] },
  {
    to: "/translation-rules",
    label: "Правила перевода",
    Icon: Languages,
    match: ["/translation-rules"],
  },
];

const ADMIN_NAV = [
  {
    to: "/ai-hints",
    label: "Подсказки для системы",
    Icon: Lightbulb,
    match: ["/ai-hints"],
  },
  { to: "/field-schema", label: "Поля", Icon: Layers, match: ["/field-schema"] },
  { to: "/admin", label: "Админка", Icon: LayoutDashboard, match: ["/admin"] },
];

const isActive = (pathname, match) =>
  match.some((m) => (m === "/" ? pathname === "/" : pathname.startsWith(m)));

const NavItem = ({ item, pathname }) => {
  const active = isActive(pathname, item.match);
  const { Icon } = item;
  return (
    <Link
      to={item.to}
      className={`px-nav-link${active ? " is-active" : ""}`}
      aria-label={item.label}
      aria-current={active ? "page" : undefined}
      data-label={item.label}
    >
      <Icon size={24} strokeWidth={2} aria-hidden="true" />
    </Link>
  );
};

const AppShell = ({ children }) => {
  const { user, logout, getTelegramLink, checkAuth } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Окно открываем сразу по клику: открытое после await браузер блокирует.
  // Кнопка пропадёт, когда пользователь вернётся из Telegram.
  const handleConnectTelegram = async () => {
    const tab = window.open("", "_blank");
    try {
      const url = await getTelegramLink();
      if (tab) tab.location.href = url;
      else window.location.href = url;
      window.addEventListener("focus", () => checkAuth(false), { once: true });
    } catch (err) {
      tab?.close();
      alert(err.response?.data?.error || "Не удалось получить ссылку на бота");
    }
  };

  return (
    <div className="px-shell">
      <nav className="px-sidebar" aria-label="Основная навигация">
        <div className="px-nav">
          {NAV.map((item) => (
            <NavItem key={item.to} item={item} pathname={pathname} />
          ))}
          {user?.is_admin && (
            <>
              <span className="px-nav-sep" aria-hidden="true" />
              {ADMIN_NAV.map((item) => (
                <NavItem key={item.to} item={item} pathname={pathname} />
              ))}
            </>
          )}
        </div>

        <div className="px-sidebar-bottom">
          {user && !user.telegram_linked && (
            <button
              type="button"
              className="px-nav-link"
              aria-label="Подключить Telegram"
              data-label="Подключить Telegram"
              onClick={handleConnectTelegram}
            >
              <Send size={24} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="px-nav-link"
            aria-label="Выйти"
            data-label="Выйти"
            onClick={handleLogout}
          >
            <LogOut size={24} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </nav>

      <main className="px-panel">{children}</main>
    </div>
  );
};

export default AppShell;
